import { useEffect, useMemo, useState } from 'react'
import { statusesAll } from 'settings'
import { DateRange } from 'react-day-picker';

export type CheckedItem = { type: string; label: string; count: number; value: string }

export type UseCertificatesFilterStateParams = {
  certificates: Array<{
    name?: string
    id: string
    /** VRG-286 — the CHIP UID. Authenticated payloads only (VRG-231). */
    tag_uid?: string
    product: { type: string; title?: string; images?: string[]; sku?: string }
    status: string
    type: string
  }>
  filteredItems: CheckedItem[]
  initialSelectedCertificateIds?: string[]
  initialActivityFrom?: string | null
  initialActivityTo?: string | null
  onFilterSubmit: (
    statusString: string,
    productString: string,
    filteredItems: CheckedItem[],
    certificateIds?: string,
    activityFrom?: string | null,
    activityTo?: string | null
  ) => void
  onCertificateFilterChange?: (ids: string[]) => void
  isFilterChanged?: boolean
}

export function useCertificatesFilterState({
  certificates,
  filteredItems,
  initialSelectedCertificateIds = [],
  initialActivityFrom = null,
  initialActivityTo = null,
  onFilterSubmit,
  onCertificateFilterChange,
  isFilterChanged,
}: UseCertificatesFilterStateParams) {
  const statusesDefault = useMemo(() => certificates.map(s => s.status), [certificates])
  const productDefault = useMemo(() => certificates.map(c => c.product.type), [certificates])

  const [filters, setFilters] = useState<any[]>([])
  const [statuses, setStatuses] = useState<string[]>(statusesDefault)
  const [products, setProducts] = useState<string[]>(productDefault)
  const [checkedItems, setCheckedItems] = useState<CheckedItem[]>(filteredItems)
  const [isFilterChange, setIsFilterChange] = useState<boolean>(false)
  const [filteredArray, setFilteredArray] = useState<any[]>([])

  const [showCertificatePicker, setShowCertificatePicker] = useState<boolean>(false)
  const [certificateQuery, setCertificateQuery] = useState<string>('')
  const [selectedCertificateIds, setSelectedCertificateIds] = useState<string[]>(initialSelectedCertificateIds)
  const [showAllChips, setShowAllChips] = useState<boolean>(false)
  const [showNfcScan, setShowNfcScan] = useState<boolean>(false)
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false)
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: initialActivityFrom ? new Date(initialActivityFrom) : undefined,
    to: initialActivityTo ? new Date(initialActivityTo) : undefined,
  });
  const [selectedRange, setSelectedRange] = useState<'last_month' | 'last_3m' | 'last_6m' | 'last_12m' | null>(null)
  const [desktopSearchFocused, setDesktopSearchFocused] = useState<boolean>(false)

  // Initialize from external activity params
  useEffect(() => {
    if (!initialActivityFrom || !initialActivityTo) return
    try {
      const from = new Date(initialActivityFrom)
      const to = new Date(initialActivityTo)
      const sameDay = from.toDateString() === to.toDateString()
      if (sameDay) {
        setDateRange({ from: from, to: to })
        setSelectedRange(null)
        return
      }
      const now = new Date()
      const diffToNowMs = Math.abs(now.getTime() - to.getTime())
      const approxNow = diffToNowMs < 48 * 60 * 60 * 1000
      const months = (now.getFullYear() - from.getFullYear()) * 12 + (now.getMonth() - from.getMonth())
      if (approxNow && [1, 3, 6, 12].includes(months)) {
        const map: Record<number, typeof selectedRange> = { 1: 'last_month', 3: 'last_3m', 6: 'last_6m', 12: 'last_12m' }
        setSelectedRange(map[months] || null)
        setDateRange(undefined)
      } else {
        setSelectedRange(null)
        setDateRange(undefined)
      }
    } catch {}
  }, [initialActivityFrom, initialActivityTo])

  useEffect(() => {
    setFiltersDefault(statuses, products)
  }, [isFilterChange, statuses, products, certificates])

  useEffect(() => {
    if (isFilterChanged && checkedItems.length > 0) {
      updateFilters(checkedItems, checkedItems[0])
    }
  }, [isFilterChanged, checkedItems])

  const updateFilters = (items: CheckedItem[], changed?: { type?: string; label?: string; count?: number }) => {
    const checkedValue = items.map(item => item.value)
    const statusChecked = items.some(item => item.type === 'status')
    const productChecked = items.some(item => item.type === 'product')

    let stat: string[]
    let prod: string[] = []

    const getStatuses = () => certificates.map(item => item.status)
    const getProducts = () => certificates.map(item => item.product.type)
    const getFilteredStatuses = () => certificates.filter(item => checkedValue.includes(item.product.type)).map(item => item.status)
    const getFilteredProducts = () => certificates.filter(item => checkedValue.includes(item.status)).map(item => item.product.type)

    if (changed?.type === 'status' && items.length > 0) {
      stat = productChecked && statusChecked ? getStatuses() : productChecked ? getFilteredStatuses() : getStatuses()
      prod = productChecked && statusChecked ? getFilteredProducts() : productChecked ? getProducts() : getFilteredProducts()
    } else if (changed?.type === 'product' && items.length > 0) {
      stat = statusChecked ? getStatuses() : getFilteredStatuses()
      prod = statusChecked ? getFilteredProducts() : getProducts()
    } else {
      stat = getStatuses()
      prod = getProducts()
    }

    setStatuses(stat)
    setProducts(prod)
    items.length > 0 ? setFilteredArray(foundedFilters(certificates as any, items)) : setFilteredArray([])
  }

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    item: { label: string; value: string; count: number },
    filter: string
  ) => {
    const checkedItem: CheckedItem = { type: filter, label: item.label, value: item.value, count: item.count }
    if (event.target.checked || isFilterChange) {
      const next = [...checkedItems, checkedItem]
      setCheckedItems(next)
      updateFilters(next, checkedItem)
    } else {
      const next = checkedItems.filter(checked => checked.value !== checkedItem.value)
      setCheckedItems(next)
      updateFilters(next.filter(checked => checked.label !== checkedItem.label), checkedItem)
    }
    setIsFilterChange(!isFilterChange)
  }

  const setFiltersDefault = (statuses: string[], products: string[]) => {
    const mobile = false // Hook no conoce el modo; construye full filtros para desktop
    if (mobile) {
      setFilters([
        { title: 'Certificate', type: 'certificate', list: [] },
        { title: 'Last activity date', type: 'last_activity', list: [] },
      ])
      return
    }
    const uniqueProductList = certificates?.map(cert => cert.product.type).filter((v, i, a) => a.indexOf(v) === i)
    const next = [
      {
        title: 'Status',
        type: 'status',
        list: statusesAll.map(status => ({ label: status.label, value: status.value, count: statuses.filter(s => s === status.value).length })),
      },
      {
        title: 'Product type',
        type: 'product',
        list: uniqueProductList?.map(type => ({ label: type, value: type, count: products.filter(p => p === type).length })),
      },
    ]
    setFilters(next as any)
  }

  const foundedFilters = (data: any[], filters: CheckedItem[]) => {
    if (filters.length === 0) return data
    let filteredData = data
    const selectedTypes = filters.filter(f => f.type === 'product').map(f => f.value)
    const statusFilters = filters.filter(f => f.type === 'status').map(f => f.value)
    if (statusFilters.length > 0) filteredData = filteredData.filter(item => statusFilters.includes(item.status))
    if (selectedTypes.length > 0) filteredData = filteredData.filter(item => selectedTypes.includes(item.product.type))
    return filteredData
  }

  // VERA-315: Reset clears the drawer's fields and nothing else — it does not
  // commit, and it does not close the drawer (the old onFilterSubmit call did
  // both). `submitFilters` remains the only commit point in this hook; under
  // auto-apply the CALLER schedules it after this runs (see `handleReset` in
  // CertificatesFilter), which is what makes the commit see the cleared state
  // rather than the state React has not flushed yet.
  //
  // It used to call `onCertificateFilterChange?.([])` here, which is the page's
  // committed setter, so Reset wrote straight through to the applied selection
  // while leaving `submitFilters` to commit everything else — one control, two
  // commit paths, firing at different times.
  const resetFilters = () => {
    setCheckedItems([])
    setFilteredArray([])
    setSelectedCertificateIds([])
    setSelectedRange(null)
    setDateRange(undefined)
  }

  const submitFilters = () => {
    const statusArray = checkedItems.filter(item => item.type === 'status').map(item => item.value)
    const productArray = checkedItems.filter(item => item.type === 'product').map(item => item.value)
    const statuses = statusArray.length > 0 ? `${statusArray.join(',')}` : ''
    const products = productArray.length > 0 ? `${productArray.join(',')}` : ''
    const certificateIds = selectedCertificateIds.length > 0 ? selectedCertificateIds.join(',') : ''
    let activityFrom: string | null = null
    let activityTo: string | null = null
    const now = new Date()
    if (selectedRange) {
      const from = new Date(now)
      if (selectedRange === 'last_month') from.setMonth(from.getMonth() - 1)
      if (selectedRange === 'last_3m') from.setMonth(from.getMonth() - 3)
      if (selectedRange === 'last_6m') from.setMonth(from.getMonth() - 6)
      if (selectedRange === 'last_12m') from.setMonth(from.getMonth() - 12)
      activityFrom = from.toISOString()
      activityTo = now.toISOString()
    } else if (dateRange?.from && dateRange?.to) {
      activityFrom = dateRange.from.toISOString()
      activityTo = dateRange.to.toISOString()
    }
    onFilterSubmit(statuses, products, checkedItems, certificateIds, activityFrom, activityTo)
    onCertificateFilterChange?.(selectedCertificateIds)
  }

  return {
    // data
    filters,
    statuses,
    products,
    checkedItems,
    isFilterChange,
    filteredArray,
    showCertificatePicker,
    certificateQuery,
    selectedCertificateIds,
    showAllChips,
    showNfcScan,
    showDatePicker,
    dateRange,
    selectedRange,
    desktopSearchFocused,
    // setters / handlers
    setShowCertificatePicker,
    setCertificateQuery,
    setSelectedCertificateIds,
    setShowAllChips,
    setShowNfcScan,
    setShowDatePicker,
    setDateRange,
    setSelectedRange,
    setDesktopSearchFocused,
    handleChange,
    resetFilters,
    submitFilters,
  }
}

export default useCertificatesFilterState


